/**
 * What each match is worth.
 *
 * The payouts were transcribed from the info sheet and then hard-coded, which
 * is fine right up until the buy-in changes or somebody argues about the
 * halved-match arithmetic on the 18th green. The money leaderboard is derived
 * from these numbers, so they have to be the group's, not the seed file's.
 *
 * Shown per match rather than per format: two rounds can share a format and
 * still be worth different money, and the sheet does exactly that.
 */

import { flightProblems } from '../engine/leaderboard'
import { matchCounts } from '../engine/types'
import type { Match, PlacePayout, Tournament } from '../engine/types'

const Num = ({
  value,
  onChange,
  prefix,
  width = 'w-16',
}: {
  value: number
  onChange: (n: number) => void
  prefix?: string
  width?: string
}) => (
  <label className="inline-flex items-center gap-1">
    {prefix && <span className="text-mute text-[12px]">{prefix}</span>}
    <input
      type="number"
      inputMode="numeric"
      value={value}
      onChange={(e) => onChange(Number(e.target.value) || 0)}
      className={`${width} border border-ink px-1 py-1 bc-num text-xs bc-tap`}
    />
  </label>
)

function PlaceRows({
  rows,
  onChange,
}: {
  rows: PlacePayout[]
  onChange: (next: PlacePayout[]) => void
}) {
  const patch = (i: number, p: Partial<PlacePayout>) =>
    onChange(rows.map((r, j) => (i === j ? { ...r, ...p } : r)))

  return (
    <div className="grid gap-1.5">
      {rows.map((r, i) => (
        <div key={i} className="flex items-center gap-2 flex-wrap">
          <span className="bc-label text-mute w-20 shrink-0">
            {r.from === r.to ? `${r.from}` : `${r.from}–${r.to}`}
            {r.from === 1 ? 'st' : r.from === 2 ? 'nd' : r.from === 3 ? 'rd' : 'th'}
          </span>
          <Num value={r.points} onChange={(n) => patch(i, { points: n })} prefix="pts" />
          <Num value={r.cash} onChange={(n) => patch(i, { cash: n })} prefix="$" width="w-20" />
          <button
            type="button"
            onClick={() => onChange(rows.filter((_, j) => j !== i))}
            className="text-mute px-1 bc-tap"
            aria-label={`Remove the ${r.from} place band`}
          >
            ×
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => {
          const last = rows[rows.length - 1]
          const from = last ? last.to + 1 : 1
          onChange([...rows, { from, to: from, points: 0, cash: 0 }])
        }}
        className="bc-label text-mute text-left bc-tap py-1"
      >
        + add a place
      </button>
    </div>
  )
}

/** The payout editor for one match, shaped by that match's format. */
export function PayoutEditor({
  match,
  onChange,
  playerCount,
}: {
  match: Match
  onChange: (config: Match['config']) => void
  /** Active players, so flight coverage can be checked against the real field. */
  playerCount: number
}) {
  const c = match.config

  if (
    c.kind === 'individualMatchPlay' ||
    c.kind === 'teamCombinedNet' ||
    c.kind === 'teamFourBall' ||
    c.kind === 'teamHighLow'
  ) {
    const p = c.payout
    /**
     * A tie splits the win, so the halve row is derived rather than typed.
     *
     * It used to be three independent rows, which let the halve drift into
     * something that was not half of anything — and nothing downstream would
     * have noticed, because a halved match is not wrong-looking, just wrong.
     */
    const setWin = (v: { points?: number; cash?: number }) => {
      const win = { ...p.win, ...v }
      onChange({
        ...c,
        payout: { ...p, win, halve: { points: win.points / 2, cash: win.cash / 2 } },
      })
    }
    const setLoss = (v: { points?: number; cash?: number }) =>
      onChange({ ...c, payout: { ...p, loss: { ...p.loss, ...v } } })

    const half = (n: number) => (Number.isInteger(n) ? `${n}` : n.toFixed(2).replace(/\.?0+$/, ''))

    return (
      <div className="grid gap-1.5">
        <div className="flex items-center gap-2">
          <span className="bc-label text-mute w-20 shrink-0">win</span>
          <Num value={p.win.points} onChange={(n) => setWin({ points: n })} prefix="pts" />
          <Num value={p.win.cash} onChange={(n) => setWin({ cash: n })} prefix="$" width="w-20" />
        </div>
        <div className="flex items-center gap-2">
          <span className="bc-label text-mute w-20 shrink-0">halve</span>
          <span className="bc-num text-xs text-mute">
            {half(p.win.points / 2)} pts · ${half(p.win.cash / 2)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="bc-label text-mute w-20 shrink-0">loss</span>
          <Num value={p.loss.points} onChange={(n) => setLoss({ points: n })} prefix="pts" />
          <Num value={p.loss.cash} onChange={(n) => setLoss({ cash: n })} prefix="$" width="w-20" />
        </div>
        <p className="text-[12px] text-mute leading-snug">
          Paid per player. A tie splits the win — both sides take half the points and
          half the money — so the halve row follows the win rather than being set on
          its own.
        </p>
      </div>
    )
  }

  if (c.kind === 'quota') {
    return (
      <>
        <PlaceRows rows={c.payouts} onChange={(payouts) => onChange({ ...c, payouts })} />
        <p className="text-[12px] text-mute leading-snug mt-1">
          Ranked against{' '}
          {c.rankingScope === 'group' ? 'the rest of your group' : `all ${playerCount} in the field`}.
        </p>
      </>
    )
  }

  // Championship: a ladder per flight, with the flight's seeding range.
  return (
    <div className="grid gap-3">
      {c.flights.map((f, fi) => (
        <div key={fi}>
          <div className="flex items-center gap-2 mb-1">
            <input
              value={f.name}
              onChange={(e) =>
                onChange({
                  ...c,
                  flights: c.flights.map((x, j) =>
                    j === fi ? { ...x, name: e.target.value } : x,
                  ),
                })
              }
              className="bc-name text-xs border-b border-rule py-0.5 bc-tap w-24"
            />
            <span className="text-[12px] text-mute">seeds</span>
            <Num
              value={f.fromSeed}
              width="w-12"
              onChange={(n) =>
                onChange({
                  ...c,
                  flights: c.flights.map((x, j) => (j === fi ? { ...x, fromSeed: n } : x)),
                })
              }
            />
            <Num
              value={f.toSeed}
              width="w-12"
              onChange={(n) =>
                onChange({
                  ...c,
                  flights: c.flights.map((x, j) => (j === fi ? { ...x, toSeed: n } : x)),
                })
              }
            />
          </div>
          <PlaceRows
            rows={f.payouts}
            onChange={(payouts) =>
              onChange({
                ...c,
                flights: c.flights.map((x, j) => (j === fi ? { ...x, payouts } : x)),
              })
            }
          />
        </div>
      ))}
      {/*
        The prose below was the only defense these ranges had. It asked the
        admin to be careful about a $4,050 purse and then let anything through.
      */}
      {(() => {
        const problems = flightProblems(c.flights, playerCount)
        if (problems.length === 0) return null
        return (
          <div className="bg-signal text-paper px-3 py-2">
            <p className="bc-label">
              {problems.length === 1 ? 'A flight problem' : `${problems.length} flight problems`}
            </p>
            <ul className="text-[12px] leading-snug mt-1 list-disc pl-4">
              {problems.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
            {/*
              It said "Setup will not save until these are fixed", which is
              simply untrue — saving is not gated on anything. Starting the week
              is, and that is the honest sentence.
            */}
            <p className="text-[12px] leading-snug mt-1">
              The week cannot be started until these are fixed.
            </p>
          </div>
        )
      })()}
      <p className="text-[12px] text-mute leading-snug">
        Seeds come from the standings going into the final, so these ranges have to
        cover every player or someone finishes in no flight and is paid nothing.
      </p>
    </div>
  )
}

/** Everything the week pays out, totalled, as a sanity check against the buy-in. */
/**
 * What the payout tables commit the week to spending, before a ball is struck.
 *
 * Pulled out of the component because it is a domain sum, not a rendering
 * detail, and because it was wrong in a way only arithmetic can catch: see the
 * note on group-scoped quota below.
 */
export function scheduledPayout(doc: Tournament): number {
  let cash = 0
  for (const m of doc.matches) {
    // A round played for fun commits the week to nothing, whatever ladder its
    // format happens to carry.
    if (!matchCounts(m)) continue
    const c = m.config
    if (c.kind === 'quota') {
      const ladder = c.payouts.reduce((s, p) => s + p.cash * (p.to - p.from + 1), 0)
      /*
       * A group-scoped quota is one contest PER FOURSOME, and each one pays the
       * whole ladder — three firsts, three seconds, three thirds.
       *
       * Counting the ladder once had Setup reporting $350 of the pot
       * unallocated on a week that actually pays $150 more than it takes in: a
       * $500 error, in the direction that tells you it is safe. Found by
       * scoring a whole tournament and adding up what came out.
       */
      const contests =
        c.rankingScope === 'group'
          ? Math.max(1, doc.groups.filter((g) => g.roundId === m.roundId).length)
          : 1
      cash += ladder * contests
    } else if (c.kind === 'strokeNet') {
      cash += c.flights.reduce(
        (s, f) => s + f.payouts.reduce((t, p) => t + p.cash * (p.to - p.from + 1), 0),
        0,
      )
    } else {
      // Head to head: every player is on one side of a match, and the losers pay
      // nothing, so the round's outlay is the winners' share of the field.
      const units = doc.pairings.filter((p) => p.matchId === m.id)
      const winners = units.reduce((s, p) => s + (p.sides[0]?.playerIds.length ?? 0), 0)
      cash += c.payout.win.cash * winners
    }
  }
  return cash
}

export function PayoutTotals({ doc }: { doc: Tournament }) {
  const cash = scheduledPayout(doc)
  const pot = doc.settings.buyIn * doc.players.filter((p) => p.active).length

  return (
    <div className="px-4 py-3 border-t border-rule">
      <div className="flex items-baseline justify-between">
        <span className="bc-label text-mute">Pot at ${doc.settings.buyIn} a head</span>
        <span className="bc-num">${pot.toLocaleString()}</span>
      </div>
      <div className="flex items-baseline justify-between mt-1">
        <span className="bc-label text-mute">Scheduled to pay out</span>
        <span className={`bc-num ${cash > pot ? 'text-signal' : ''}`}>
          ${cash.toLocaleString()}
        </span>
      </div>
      <p className="text-[12px] text-mute leading-snug mt-1.5">
        {cash > pot
          ? 'More is scheduled to go out than comes in. Somebody is covering the difference.'
          : cash === pot
            ? 'Balanced.'
            : `$${(pot - cash).toLocaleString()} of the pot is unallocated.`}{' '}
        {/*
          This used to say a week of halves pays less. It does not: a halve pays
          half the win to BOTH sides, so six won singles at $100 and twelve
          halved sides at $50 are the same $600. A treasurer who believed the
          old sentence would have under-reserved.
        */}
        A halve pays half the win to each side, so a round costs the same however
        it finishes — this total holds whether every match is won or every match
        is halved.
      </p>
    </div>
  )
}
